/// <amd-module name="VSS/Features/Markdown/Plugins/MarkdownMetadata" />
import * as MarkdownIt from "markdown-it";
import * as JSYaml from "js-yaml";

import { IState } from "../MarkdownIt";

export class MarkdownMetadata {

    /*
     * Grp 0: ((GRP1)(GRP2)(GRP3))?
     * Grp 1: (^\-{3}\\s*$) matches line starts with --- and have only tailing white spaces.
     * Grp 2: ((\n|.)*?) matches anything that is in between Grp1 and Grp3 including new line.
     * Grp 3: (^\-{3}\\s*$) matches line starts with --- and have only tailing white spaces.
     */
    private _yamlMetadata: RegExp = new RegExp("((^\-{3}\\s*$)((\n|.)*?)(^\-{3}\\s*$))?", 'm');
    private FULL_MATCH_GROUP_INDEX: number = 0;
    private YAML_TEXT_GROUP_INDEX: number = 3;

    private MAX_NESTING: number = 2;

    constructor(md: MarkdownIt.MarkdownIt) {
        // registering before all block rules.
        (<any>md).block.ruler.before('table', 'meta', this.parseMetadata, {});
    }

    private parseMetadata = (state: IState, startLine: number, endLine: number, silent: boolean): boolean => {
        if (startLine === 0 && state) {
            const yaml = this._extractYaml(state);
            let root: any;
            if (yaml) {
                root = this._getJsonFromYaml(yaml);
            }
            if (root) {
                this._generateTokens(state, root);
                return true;
            }
        }
        //reset processed in case of failure.
        state.line = startLine;
        //execute other rules
        return false;
    }

    //#region yamlparsing

    /**
     * Extracts yaml from @param state.src, and returns yaml enclosed by --- at beginning and end.
     * @param state Markdown-it block state.
     */
    private _extractYaml(state: IState): string | null {
        let res: RegExpExecArray | null = this._yamlMetadata.exec(state.src);
        if (res === null || res.length === 0) {
            return null;
        }
        /*
            To update number of lines processed
            we need to count number of character in regex match
            then find the index of the eMark which is processed
            greater than equal to yEmark(count of char in our yamltext).
        */
        let yEmark: number = this._getGroup(res, this.FULL_MATCH_GROUP_INDEX).length;
        let processedLines: number = 0;
        for (; processedLines < state.eMarks.length; processedLines++) {
            if (yEmark <= state.eMarks[processedLines]) {
                break;
            }
        }
        state.line = processedLines + 1;
        return this._getGroup(res, this.YAML_TEXT_GROUP_INDEX);
    }

    /**
     * Parse json from @type string and generates yaml.
     * @param yamlString
     */
    private _getJsonFromYaml(yamlString: string): any {
        try {
            return JSYaml.load(yamlString);
        } catch (e) {
            return null;
        }
    }

    /**
     * Fetches given @param grp from @param match.
     * @param match RegExpExecArray
     * @param grp index of group which is required to fetch.
     */
    private _getGroup(match: RegExpExecArray, grp: number): string {
        if (match && match.length > grp) {
            return match[grp];
        } else {
            return "";
        }
    }

    //#endregion

    //#region MarkdownTokenGenerators.
    /**
     * Generates table token stream for JSON object/list.
     * @param state Markdown block state.
     * @param root source json object or array.
     */
    private _generateTokens(state: IState, root: any): void {
        if (Array.isArray(root)) {
            this._generateListTokens(state, root);
        } else if (root && typeof root === "object") {
            this._generateObjectTokens(state, root);
        }
    }

    /**
     * Generates table for object with key in head and value in row.
     * If value is non scalar element, will generate nested table.
     * @param state markdown block state.
     * @param root source json object or array.
     * @param nesting current depth of object.
     */
    private _generateObjectTokens(state: IState, root: object, nesting: number = 0): void {
        if (nesting > this.MAX_NESTING) {
            this._createInlineToken(state, JSON.stringify(root));
            return;
        }
        // open table
        this._applyAtttibutes(state.push("table_open", "table", 1));

        // open head
        state.push("thead_open", "thead", 1);
        // open row
        state.push("tr_open", "tr", 1);
        for (let key of Object.keys(root)) {
            this._generateTokenForKey(key, state);
        }
        // end row
        state.push("tr_close", "tr", -1);
        // close head
        state.push("thead_close", "thead", -1);
        // open body
        state.push("tbody_open", "tbody", 1);
        // open row
        state.push("tr_open", "tr", 1);
        for (let key of Object.keys(root)) {
            let r = root as any;
            let val: any = r[key];
            this._generateTokensForValue(val, state, nesting + 1);
        }
        // close row
        state.push("tr_close", "tr", -1);
        // close body
        state.push("tbody_close", "tbody", -1);
        // close table
        state.push("table_close", "table", -1);
    }

    /**
     * Generates table for list with values in single row and table doesnot have head.
     * If value is non scalar element, will generate nested table by calling @method _objectTokens or @method _listTokens.
     * @param state markdown block state.
     * @param root source json object or array.
     * @param nesting current depth of object.
     */
    private _generateListTokens(state: IState, root: any, nesting: number = 0): void {
        if (nesting > this.MAX_NESTING) {
            this._createInlineToken(state, JSON.stringify(root));
            return;
        }
        // open table
        this._applyAtttibutes(state.push("table_open", "table", 1));
        // open row
        state.push("tr_open", "tr", 1);
        for (let val of root) {
            this._generateTokensForValue(val, state, nesting + 1);
        }
        // close row
        state.push("tr_close", "tr", -1);
        // close table
        state.push("table_close", "table", -1);
    }

    /**
     * Generates token for Value.
     * @param val
     * @param state
     * @param nesting current depth of object.
     */
    private _generateTokensForValue(val: any, state: IState, nesting: number): void {
        state.push("td_open", "td", 1);
        if (Array.isArray(val)) {
            this._generateListTokens(state, val, nesting);
        } else if (val && Object.prototype.toString.call(val) === '[object Date]') {
            this._createInlineToken(state, val.toLocaleString());
        } else if (val && typeof val === "object") {
            this._generateObjectTokens(state, val, nesting);
        } else {
            this._createInlineToken(state, new String(val));
        }
        state.push("td_close", "td", -1);
    }

    /**
     * Generates token for Key.
     * @param key
     * @param state
     */
    private _generateTokenForKey(key: any, state: IState): void {
        state.push("th_open", "th", 1);
        let inline: any = state.push("inline", "", 0);
        inline.content = new String(key);
        inline.children = [];
        state.push("th_close", "th", -1);
    }

    /**
     * Applies style to given token.
     * @param token
     */
    private _applyAtttibutes(token: MarkdownIt.Token): void {
        (token.attrs = token.attrs || []).push(["class", "metadata-yaml-table"]);
    }

    private _createInlineToken(state: IState, val: String): void {
        let inline: any = state.push("inline", "", 0);
        inline.content = val;
        inline.children = [];
    }
    //#endregion
}
